'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Client } from '@garage/shared'
import { clientDisplayName, whatsappNumberFor, isCompany } from '@garage/shared'
import {
  PageHeader,
  Button,
  IconButton,
  DataTable,
  TextField,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  Chip,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  PeopleIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useDebounced } from '@/lib/hooks.js'
import { ClientFormDialog } from '@/components/ClientFormDialog.js'

const PAGE_SIZE = 50

export function ClientsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const pageStr = searchParams.get('page') ?? '1'
  const page = Math.max(1, parseInt(pageStr, 10))

  const [searchInput, setSearchInput] = useState(q)
  const debouncedSearch = useDebounced(searchInput, 250)

  const [deleteClientId, setDeleteClientId] = useState<string | null>(null)
  const [editingClient, setEditingClient] = useState<Client | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)

  // Update URL search params when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== q) {
      setSearchParams({ q: debouncedSearch, page: '1' }, { replace: true })
    }
  }, [debouncedSearch, q, setSearchParams])

  const { data: clientsData, isLoading, isError } = useQuery({
    queryKey: qk.clients({ q: debouncedSearch, page, pageSize: PAGE_SIZE }),
    queryFn: () =>
      api.clients.list({ q: debouncedSearch, page, pageSize: PAGE_SIZE }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.clients.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.clients() })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      snackbar.success(t('clients.clientHidden'))
      setDeleteClientId(null)
    },
    onError: (error) => {
      snackbar.error(
        error instanceof ApiError ? error.message : t('clients.failedToDelete')
      )
    },
  })

  const handleEdit = (client: Client) => {
    setEditingClient(client)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingClient(undefined)
  }

  const handleAddNew = () => {
    setEditingClient(undefined)
    setFormDialogOpen(true)
  }

  // Determine if we're in "no results" vs "no data" state
  const hasSearchQuery = debouncedSearch.trim() !== ''
  const isEmpty = !clientsData?.items || clientsData.items.length === 0

  const columns: Column<Client>[] = [
    {
      key: 'name',
      header: t('clients.columns.name'),
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{clientDisplayName(row)}</span>
          {isCompany(row) && <Chip label={t('clients.company')} />}
        </div>
      ),
    },
    {
      key: 'phone',
      header: t('clients.columns.phone'),
      cell: (row) => <span className="ref">{row.phone}</span>,
      width: 'w-40',
    },
    {
      key: 'whatsapp',
      header: t('clients.columns.whatsapp'),
      cell: (row) => {
        const waPhone = whatsappNumberFor(row)
        if (waPhone === row.phone) {
          return (
            <span className="text-on-surface-variant text-sm">
              {t('clients.sameAsPhone')}
            </span>
          )
        }
        return <span className="ref">{waPhone}</span>
      },
      hideOnMobile: true,
      width: 'w-40',
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('clients.editClient')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('clients.deleteClient')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteClientId(row.id)
            }}
          />
        </div>
      ),
      width: 'w-24',
    },
  ]

  let emptyContent: React.ReactNode = null
  if (!isLoading && isEmpty) {
    if (hasSearchQuery) {
      emptyContent = (
        <EmptyState
          icon={<PeopleIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('clients.noClientsFound')}
          description={t('clients.adjustSearch')}
        />
      )
    } else {
      emptyContent = (
        <EmptyState
          icon={<PeopleIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('clients.noClientsYet')}
          description={t('clients.addFirstClient')}
          action={<Button variant="tonal" onClick={handleAddNew}>{t('clients.addAClient')}</Button>}
        />
      )
    }
  }

  const totalPages = clientsData
    ? Math.ceil(clientsData.total / PAGE_SIZE)
    : 0
  const startRow = (page - 1) * PAGE_SIZE + 1
  const endRow = Math.min(page * PAGE_SIZE, clientsData?.total ?? 0)
  const showingText =
    clientsData && clientsData.total > 0
      ? t('common.showingRange', { from: startRow, to: endRow, total: clientsData.total })
      : ''

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('clients.title')}
        actions={
          <Button
            variant="filled"
            icon={<AddIcon className="w-5 h-5" />}
            onClick={handleAddNew}
          >
            {t('clients.addClient')}
          </Button>
        }
      />

      <TextField
        label={t('common.search')}
        value={searchInput}
        onChange={setSearchInput}
        placeholder={t('clients.searchPlaceholder')}
        fullWidth
      />

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton lines={3} />
        </div>
      ) : isError ? (
        <EmptyState
          title={t('clients.errorLoading')}
          description={t('clients.tryRefreshing')}
        />
      ) : isEmpty ? (
        emptyContent
      ) : (
        <div className="space-y-4">
          <div className="bg-surface-container border border-outline-variant rounded-lg">
            <DataTable<Client>
              columns={columns}
              rows={clientsData!.items}
              rowKey={(c) => c.id}
              onRowClick={(client) => navigate(`/clients/${client.id}`)}
              stackOnMobile
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-body-sm text-on-surface-variant">
              {showingText}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outlined"
                onClick={() => {
                  const newPage = Math.max(1, page - 1)
                  setSearchParams({ q: debouncedSearch, page: String(newPage) })
                }}
                disabled={page <= 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const newPage = Math.min(totalPages, page + 1)
                  setSearchParams({ q: debouncedSearch, page: String(newPage) })
                }}
                disabled={page >= totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ClientFormDialog
        open={formDialogOpen}
        client={editingClient}
        onClose={handleFormClose}
      />

      <ConfirmDialog
        open={!!deleteClientId}
        title={t('clients.deleteClientTitle')}
        message={t('clients.deleteClientMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteClientId) {
            deleteMutation.mutate(deleteClientId)
          }
        }}
        onCancel={() => setDeleteClientId(null)}
      />
    </div>
  )
}
